import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Bundle} from '@sportngin/se-commerce-sdk/models/clare';
import {environment} from '../../../environments/environment';
import {ApiResponse, PagedApiResponse} from '../../models/clare';
import {ListBundlesParams} from '../../models/clare/item-query-params';
import {
  BundleProductsUpdateRequest,
  BundleSummary,
  CreateBundleRequest,
  stripBundleProductsFromList,
  UpdateBundleRequest,
} from '../../models/clare/bundle';
import {objectToHttpParams, pluckResult, ProductManagementApiService} from '../product-management-api.service';

@Injectable({
  providedIn: 'root',
})
export class BundleApiService extends ProductManagementApiService {
  readonly controllerPath = '';

  constructor(protected override http: HttpClient) {
    super(http);
  }

  create(body: CreateBundleRequest, organizationId: string): Observable<Bundle> {
    const url = this.getUrl(`/finance/organizations/${organizationId}/bundles`);
    return this.POST<ApiResponse<Bundle>>(url, body, this.requestOptions()).pipe(pluckResult());
  }

  list(params?: ListBundlesParams): Observable<Bundle[]> {
    const url = this.getUrl('/finance/bundles');
    const options = params ? {...this.requestOptions(), params: objectToHttpParams(params)} : this.requestOptions();

    return this.GET<ApiResponse<Bundle[]>>(url, options).pipe(
      map(res => stripBundleProductsFromList(Array.isArray(res?.result) ? res.result : []))
    );
  }

  /**
   * Backwards-compatible alias for list used by existing product list loaders.
   */
  getBundles(params?: ListBundlesParams): Observable<Bundle[]> {
    return this.list(params);
  }

  /**
   * Lists bundles with pagination metadata preserved.
   */
  listPaged(params?: ListBundlesParams): Observable<PagedApiResponse<Bundle>> {
    const url = this.getUrl('/finance/bundles');
    const options = params ? {...this.requestOptions(), params: objectToHttpParams(params)} : this.requestOptions();
    return this.GET<PagedApiResponse<Bundle>>(url, options).pipe(
      map(res => ({
        ...res,
        result: stripBundleProductsFromList(Array.isArray(res?.result) ? res.result : []),
      }))
    );
  }

  /**
   * Fetches a single bundle by id.
   */
  getBundle(id: number | string, organizationId?: string): Observable<Bundle> {
    const url = this.getUrl(this.bundlePath(id, organizationId));
    return this.GET<ApiResponse<Bundle>>(url, this.requestOptions()).pipe(pluckResult());
  }

  /**
   * Updates a bundle by id.
   */
  updateBundle(id: number | string, body: UpdateBundleRequest, organizationId?: string): Observable<Bundle> {
    const url = this.getUrl(this.bundlePath(id, organizationId));
    return this.PUT<ApiResponse<Bundle>>(url, body, this.requestOptions()).pipe(pluckResult());
  }

  /**
   * Adds products to a bundle.
   */
  addProductsToBundle(
    bundleId: number | string,
    body: BundleProductsUpdateRequest,
    organizationId?: string
  ): Observable<Bundle> {
    const url = this.getUrl(`${this.bundlePath(bundleId, organizationId)}/add_products`);
    return this.PUT<ApiResponse<Bundle>>(url, body, this.requestOptions()).pipe(pluckResult());
  }

  /**
   * Removes products from a bundle.
   */
  removeProductsFromBundle(
    bundleId: number | string,
    body: BundleProductsUpdateRequest,
    organizationId?: string
  ): Observable<Bundle> {
    const url = this.getUrl(`${this.bundlePath(bundleId, organizationId)}/remove_products`);
    return this.PUT<ApiResponse<Bundle>>(url, body, this.requestOptions()).pipe(pluckResult());
  }

  getBundlesByProductId(productId: string): Observable<BundleSummary[]> {
    const url = this.getUrl(`/finance/products/${productId}/bundles`);
    return this.GET<ApiResponse<BundleSummary[]>>(url, this.requestOptions()).pipe(pluckResult());
  }

  private bundlePath(bundleId: number | string, organizationId?: string): string {
    return organizationId
      ? `/finance/organizations/${organizationId}/bundles/${bundleId}`
      : `/finance/bundles/${bundleId}`;
  }

  protected override getUrl(path = ''): string {
    return `${environment.apiBaseUrl}${path}`;
  }
}
